import { DataCluster } from "./DataCluster";
import { MarkerUtils } from "./MarkerUtils";
import { OverlayViewSafe } from "./OverlayViewSafe";
import { ClusterStats, DefaultRenderer, Renderer } from "./Renderer";
import { Algorithm, AlgorithmOptions } from "./algorithms/core";
import { SuperClusterAlgorithm } from "./algorithms/supercluster";
import { SuperClusterViewportAlgorithm } from "./algorithms/superviewport";

export type onClusterClickHandler = (
    event: google.maps.MouseEvent,
    cluster: DataCluster,
    map: google.maps.Map
) => void;

export interface MarkerClustererOptions {
    lstPoints?: google.maps.Data.Feature[];
    algorithm?: Algorithm;
    algorithmOptions?: AlgorithmOptions;
    map?: google.maps.Map | null;
    renderer?: Renderer;
    onClusterClick?: onClusterClickHandler;
}

export enum MarkerClustererEvents {
    CLUSTERING_BEGIN = "clusteringbegin",
    CLUSTERING_END = "clusteringend",
    CLUSTER_CLICK = "click"
}

export const defaultOnClusterClickHandler: onClusterClickHandler = (
    _: google.maps.MouseEvent,
    cluster: DataCluster,
    map: google.maps.Map
): void => {
    map.fitBounds(cluster.bounds);
};

export class MarkerClusterer extends OverlayViewSafe{
    public onClusterClick: onClusterClickHandler;
    protected algorithm: Algorithm;
    protected clusters: DataCluster[];
    protected lstPoints: google.maps.Data.Feature[];
    protected renderer: Renderer;
    protected map: google.maps.Map | null;
    protected idleListener: google.maps.MapsEventListener;

    constructor({
        map,
        lstPoints = [],
        algorithmOptions = {},
        algorithm = new SuperClusterViewportAlgorithm(algorithmOptions),
        renderer = new DefaultRenderer,
        onClusterClick = defaultOnClusterClickHandler
    }: MarkerClustererOptions){
        super();
        this.lstPoints = [...lstPoints];
        this.clusters = [];

        this.algorithm = algorithm;
        this.renderer = renderer;

        this.onClusterClick = onClusterClick;

        if(map){
            this.setMap(map);
        }
    }

    //
    // Passed current list of data points
    // Figures out what changed and updates the list of points
    // Then renders the points
    //
    public mergeMarkers(Type: string, lstInputPoints: google.maps.Data.Feature[], noDraw?: boolean, ForceRedraw: boolean = false): void{
        var lstToAdd: google.maps.Data.Feature[] = [];
        var lstToRemove: google.maps.Data.Feature[] = [];

        lstToAdd = lstInputPoints.filter(point => this.CheckInList(Type, point, this.lstPoints) == false);
        lstToRemove = this.lstPoints.filter(point => this.CheckInList(Type, point, lstInputPoints) == false);

        this.addMarkers(lstToAdd, true, true); //Skips already in check because it already filtered the values
        this.removeMarkers(lstToRemove, true);

        if(!noDraw){
            this.render(ForceRedraw);
        }
    }

    public CheckInList(Type: string, point: google.maps.Data.Feature, lstToCheck: google.maps.Data.Feature[]): boolean{
        var found = false;

        for(var z = 0; z < lstToCheck.length; z++){
            var next = lstToCheck[z];

            if(Type == "Bus"){
                if(point.getProperty("BusID") == next.getProperty("BusID")){
                    found = true;
                    break;
                }
            }
            else if(Type == "Student"){
                if(point.getProperty("BusStudentRideTxnID") == next.getProperty("BusStudentRideTxnID")){
                    found = true;
                    break;
                }
            }
        }

        return found;
    }

    public addMarker(point: google.maps.Data.Feature, noDraw?: boolean, skipCheck: boolean = false): void{

        //
        // (Can skip check if coming from merge)
        //
        if(!skipCheck){
            if(this.CheckInList(point.getProperty("Type"), point, this.lstPoints) == true){
                return;
            }
        }

        this.lstPoints.push(point);

        // No draw true when being called by another method or in a loop (Save rendering for the end of outer method)
        if(!noDraw){
            this.render();
        }
    }

    public addMarkers(lstPoints: google.maps.Data.Feature[], noDraw?: boolean, skipCheck: boolean = false): void {
        lstPoints.forEach((point) =>{
            this.addMarker(point, true, skipCheck);
        });

        // No draw true when being called by another method or in a loop (Save rendering for the end of outer method)
        if(!noDraw){
            this.render();
        }
    }

    public removeMarker(point: google.maps.Data.Feature, noDraw?: boolean): boolean {
        const index = this.lstPoints.indexOf(point);

        if(index === -1){
            return false;
        }

        MarkerUtils.setMap(point, null);
        this.lstPoints.splice(index, 1);

        // No draw true when being called by another method or in a loop (Save rendering for the end of outer method)
        if(!noDraw){
            this.render();
        }

        return true;
    }

    public removeMarkers(lstPoints: google.maps.Data.Feature[], noDraw?: boolean): boolean {
        let removed = false;

        lstPoints.forEach((point) =>{
            removed = this.removeMarker(point, true) || removed;
        });

        // No draw true when being called by another method or in a loop (Save rendering for the end of outer method)
        if(removed && !noDraw){
            this.render();
        }

        return removed;
    }

    public clearMarkers(noDraw?: boolean): void {
        this.lstPoints.length = 0;

        // No draw true when being called by another method or in a loop (Save rendering for the end of outer method)
        if(!noDraw){
            this.render();
        }
    }

    public render(ForceRedraw: boolean = false): void{
        const map = this.getMap();
        if(map instanceof google.maps.Map && map.getProjection()){
            google.maps.event.trigger(
                this,
                MarkerClustererEvents.CLUSTERING_BEGIN,
                this
            );

            //
            // Calls Calculate on SuperViewPort Algorithm
            //
            const { clusters, changed } = this.algorithm.calculate({
                lstPoints: [...this.lstPoints],
                map,
                mapCanvasProjection: this.getProjection(),
                ForceRedraw
            });

            if(changed || changed == undefined){

                //
                // Decide which points to delete later (Delayed to avoid making them flash on screen)
                //
                const lstSinglePoints = new Set<google.maps.Data.Feature>();

                for(const cluster of clusters){
                    if(cluster.lstPoints.length == 1){
                        lstSinglePoints.add(cluster.lstPoints[0]);
                    }
                }

                const lstGroupPointsToDelete: google.maps.Data.Feature[] = [];
                for(const cluster of this.clusters){
                    if(cluster.DisplayPoint == null){
                        continue;
                    }

                    if(cluster.lstPoints.length == 1){
                        //Was previously rendered and should be removed
                        if(!lstSinglePoints.has(cluster.DisplayPoint)){
                            MarkerUtils.setMap(cluster.DisplayPoint, null);
                        }
                    }
                    else{
                        lstGroupPointsToDelete.push(cluster.DisplayPoint);
                    }
                }

                //
                // Draw clusters
                //
                this.clusters = clusters;
                this.renderClusters();

                //
                // Removes old points after drawing the new ones
                //
                requestAnimationFrame(() =>
                    lstGroupPointsToDelete.forEach((point) => MarkerUtils.setMap(point, null))
                );
            }

            google.maps.event.trigger(
                this,
                MarkerClustererEvents.CLUSTERING_END,
                this
            );
        }
    }

    public onAdd(): void{
        this.idleListener = this.getMap().addListener(
            "idle",
            this.render.bind(this)
        );
        this.render();
    }

    public onRemove(): void{
        google.maps.event.removeListener(this.idleListener);
        this.reset();
    }

    protected reset(): void{
        this.lstPoints.forEach((point) => MarkerUtils.setMap(point, null));
        this.clusters.forEach((cluster) => cluster.delete());
        this.clusters = [];
    }

    protected renderClusters(): void{
        //
        // Stats can be used for coloring points
        // Ex. Highlight groups that have an above average number of points
        // (Remove if not being used in future)
        //
        const stats = new ClusterStats(this.lstPoints, this.clusters);
        const map = this.getMap() as google.maps.Map;

        this.clusters.forEach((cluster) =>{
            //
            // Single point, show the only point the list
            //
            if(cluster.lstPoints.length == 1){
                cluster.DisplayPoint = cluster.lstPoints[0];
            }
            //
            // Cluster
            //
            else{
                //
                // Create the group point
                //
                cluster.DisplayPoint = this.renderer.render(cluster, stats, map);
                
                // Hide sub-items that are part of the group
                cluster.lstPoints.forEach((point) => MarkerUtils.setMap(point, null));
                
                //
                // Click handling (already doing this on bus map page)
                //
                // if(this.onClusterClick){
                //     cluster.DisplayPoint.addListener(
                //         "Click",
                //         (event: google.maps.MouseEvent) => {
                //             google.maps.event.trigger(
                //                 this,
                //                 MarkerClustererEvents.CLUSTER_CLICK,
                //                 cluster
                //             );
                //             this.onClusterClick(event, cluster, map);
                //         }
                //     )
                // }
            }

            //
            // Display the main point (single or group)
            //
            MarkerUtils.setMap(cluster.DisplayPoint, map);
        })
    }

}